import React from 'react';

import type { FormField } from '../types/pdf.types';

interface TextFieldOverlayProps {
  field: FormField;
  value: string;
  onChange: (value: string) => void;
  scale: number;
  isZooming?: boolean;
}

const TextFieldOverlay: React.FC<TextFieldOverlayProps> = ({
  field,
  value,
  onChange,
  scale,
  isZooming = false,
}) => {
  if (field.hidden) return null;

  const [x, y, width, height] = field.rect;
  const scaledX = x * scale;
  const scaledY = y * scale;
  const scaledWidth = width * scale;
  const scaledHeight = height * scale;

  const fontSizePt = (field.fontSize || 12) * 0.5;

  const isReadOnly = field.readOnly === true;
  const isRequired = field.required === true;
  const isNoExport = field.noExport === true;
  const isPassword = field.password === true;

  const cellWidthPx =
    field.comb === true && field.maxLength && field.maxLength > 0
      ? scaledWidth / field.maxLength
      : 0;
  const characterWidthPx = fontSizePt * 1.333 * scale; // Convert pt→px with scale
  const combLetterSpacingPx =
    cellWidthPx > 0 ? Math.max(0, cellWidthPx - characterWidthPx) : 0;

  const horizontalPadding =
    field.comb === true ? `0 0 0 ${cellWidthPx * 0.1}px` : '0px';

  let overlayBorder = '1px solid transparent';
  if (isRequired) {
    overlayBorder = '1px solid #a214fa';
  } else if (isNoExport) {
    overlayBorder = '1px dashed #b37feb';
  }

  const overlayStyle = {
    left: `${scaledX}px`,
    top: `${scaledY}px`,
    width: `${scaledWidth}px`,
    height: `${scaledHeight}px`,
    padding: horizontalPadding,
    fontSize: `${fontSizePt * scale}pt`,
    fontFamily: field.fontFamily || 'Helvetica, Arial, sans-serif',
    border: overlayBorder,
    backgroundColor: isReadOnly
      ? 'rgba(240, 240, 240, 0.8)'
      : 'rgba(255, 255, 255, 0.8)',
    opacity: isZooming ? 0 : 1,
    pointerEvents:
      isZooming || isReadOnly ? ('none' as const) : ('auto' as const),
    textAlign: field.alignment || 'left',
    letterSpacing: field.comb === true ? `${combLetterSpacingPx}px` : 'normal',
    cursor: isReadOnly ? 'not-allowed' : 'text',
  };

  const titleParts = [field.name];
  if (isRequired) titleParts.push('Required');
  if (isNoExport) titleParts.push('No export');
  if (isReadOnly) titleParts.push('Read-only');
  const title = titleParts.join(' • ');

  return (
    <input
      type={isPassword ? 'password' : 'text'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="pdf-field-overlay pdf-field-input"
      style={overlayStyle}
      title={title}
      {...(field.maxLength && field.maxLength > 0
        ? { maxLength: field.maxLength }
        : {})}
      readOnly={isReadOnly}
      required={isRequired}
      aria-required={isRequired}
      spellCheck={field.doNotSpellCheck ? false : undefined}
    />
  );
};

const areEqual = (
  prevProps: TextFieldOverlayProps,
  nextProps: TextFieldOverlayProps,
) => {
  return (
    prevProps.field.id === nextProps.field.id &&
    prevProps.value === nextProps.value &&
    prevProps.scale === nextProps.scale &&
    prevProps.isZooming === nextProps.isZooming
  );
};

export default React.memo(TextFieldOverlay, areEqual);
